// Community Management page — the "Community" side-menu feature.
//
// Two submenus, selected via the `view` prop (the routes pass 'prospect' or
// 'active'):
//   - Prospects: crawler-surfaced possible-fit communities awaiting approval.
//     Columns: name, description, brand, link to API/account creation,
//     importance, credentials (for manual login), automation index. Each row
//     can be approved (→ Active) or removed.
//   - Active: approved/engaged communities. Shows the active posts/threads
//     with a reply option per post, ranked by activity, plus the latest
//     activity timestamp.
//
// Gated by community* permissions, favouritable (mirrors Brands/Ideas).
import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Modal } from "antd";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import { computeFinalPermissions } from "helpers/finalPermissions";
import addNotification from "helpers/notify";
import CommunityChannels from "./CommunityChannels";
import CommunityAnalytics from "./CommunityAnalytics";
import JobPipeline from "./JobPipeline";
import CommunityInbox from "./CommunityInbox";
import CommunityContacts from "./CommunityContacts";
import CommunityAutomation from "./CommunityAutomation";
import CommunityConnectors from "./CommunityConnectors";
import CommunityTabs from "./CommunityTabs";
import {
  listCommunities,
  createCommunity,
  deleteCommunity,
  toggleFavourite,
  discoverCommunities,
  approveCommunity,
  getCommunityCredentials,
  requestAiReply,
  sendReply,
  getConnectorCatalog,
  connectChannel,
  disconnectChannel,
  getMessages,
  sendMessage,
} from "api/communities/communities";

export const PLATFORM_LABELS = {
  skool: "Skool", quora: "Quora", reddit: "Reddit", discord: "Discord",
  slack: "Slack", facebook: "Facebook", linkedin: "LinkedIn",
  indiehackers: "Indie Hackers", stackexchange: "Stack Exchange",
  forum: "Forum", whatsapp: "WhatsApp", telegram: "Telegram",
  signal: "Signal", other: "Other",
};

// Every platform the manual "Add community" form can target — mirrors the
// model's PLATFORMS enum so the user can register a new source (Skool, Quora,
// Reddit, a niche forum, …) by hand without the crawler.
export const PLATFORMS = Object.keys(PLATFORM_LABELS);

// How a manually-added community is engaged: a logged-in account (headless
// login → search → post) or a platform API connector (read/search/DM/reply/
// post). The live messaging 'connector' type is wired up separately on Active
// rows, so the add form only offers the two the user picks up front.
export const CONNECTION_TYPES = ["account", "api"];

// A blank community for the manual add form. Manual adds opt the brand in, so
// they default straight to 'active' (the crawler is what creates 'prospect'
// rows). automationIndex defaults to 5 (no automation) until the user wires an
// API; importance starts mid-scale.
export const emptyCommunity = (brandId) => ({
  brand_id: brandId || "",
  name: "",
  description: "",
  platform: "reddit",
  url: "",
  connectionType: "account",
  loginUrl: "",
  username: "",
  password: "",
  apiConnectorUrl: "",
  apiKey: "",
  importance: 50,
  automationIndex: 5,
  status: "active",
});

// The live messaging channels that can be wired to an Active community.
const LIVE_CHANNELS = ["whatsapp", "discord", "telegram", "signal"];

// Presentational add/edit form. Kept pure (no redux/network) so it unit-tests
// in isolation; the parent owns state and submission. `brands` populates the
// brand picker; `hasPassword`/`hasApiKey` hide a stored secret behind a
// "leave blank to keep" placeholder on edit.
export function CommunityEditForm({ value, onChange, brands = [], hasPassword, hasApiKey }) {
  const v = value || {};
  const set = (field) => (e) => onChange && onChange(field, e.target.value);
  const setNum = (field) => (e) => onChange && onChange(field, Number(e.target.value) || 0);

  return (
    <div className="community-edit">
      <div className="form-group mb-2">
        <label className="d-block f-14">Community name *</label>
        <input aria-label="Community name" className="form-control"
          value={v.name || ""} onChange={set("name")} placeholder="e.g. r/SaaS, Skool — Founders" />
      </div>

      <div className="form-group mb-2">
        <label className="d-block f-14">Description</label>
        <textarea aria-label="Description" className="form-control" rows={2}
          value={v.description || ""} onChange={set("description")} />
      </div>

      <div className="form-group mb-2">
        <label className="d-block f-14">Brand</label>
        <select aria-label="Brand" className="form-control" value={v.brand_id || ""} onChange={set("brand_id")}>
          <option value="">— Unassigned —</option>
          {brands.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
        </select>
      </div>

      <div className="form-group mb-2">
        <label className="d-block f-14">Platform</label>
        <select aria-label="Platform" className="form-control" value={v.platform || "reddit"} onChange={set("platform")}>
          {PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABELS[p]}</option>)}
        </select>
      </div>

      <div className="form-group mb-2">
        <label className="d-block f-14">Link to thread / post / forum page</label>
        <input aria-label="Community URL" className="form-control" value={v.url || ""}
          onChange={set("url")} placeholder="https://…" />
      </div>

      <div className="form-group mb-2">
        <label className="d-block f-14">Connection type</label>
        <select aria-label="Connection type" className="form-control"
          value={v.connectionType || "account"} onChange={set("connectionType")}>
          <option value="account">Account (login &amp; post)</option>
          <option value="api">API connector (read / search / DM / post)</option>
        </select>
      </div>

      {v.connectionType === "api" ? (
        <>
          <div className="form-group mb-2">
            <label className="d-block f-14">API / account creation URL</label>
            <input aria-label="API connector URL" className="form-control"
              value={v.apiConnectorUrl || ""} onChange={set("apiConnectorUrl")} placeholder="https://…" />
          </div>
          <div className="form-group mb-2">
            <label className="d-block f-14">API key</label>
            <input aria-label="API key" type="password" autoComplete="new-password" className="form-control"
              value={v.apiKey || ""} onChange={set("apiKey")}
              placeholder={hasApiKey ? "•••••• (leave blank to keep)" : ""} />
          </div>
        </>
      ) : (
        <>
          <div className="form-group mb-2">
            <label className="d-block f-14">Login URL</label>
            <input aria-label="Login URL" className="form-control" value={v.loginUrl || ""}
              onChange={set("loginUrl")} placeholder="https://…/login" />
          </div>
          <div className="form-group mb-2">
            <label className="d-block f-14">Username / email</label>
            <input aria-label="Username" className="form-control" value={v.username || ""} onChange={set("username")} />
          </div>
          <div className="form-group mb-2">
            <label className="d-block f-14">Password (kept visible for manual login)</label>
            <input aria-label="Password" type="password" autoComplete="new-password" className="form-control"
              value={v.password || ""} onChange={set("password")}
              placeholder={hasPassword ? "•••••• (leave blank to keep)" : ""} />
          </div>
        </>
      )}

      <div className="d-flex" style={{ gap: 8 }}>
        <div className="form-group mb-2 flex-fill">
          <label className="d-block f-14">Importance (0–100)</label>
          <input aria-label="Importance" type="number" min={0} max={100} className="form-control"
            value={v.importance ?? 0} onChange={setNum("importance")} />
        </div>
        <div className="form-group mb-2 flex-fill">
          <label className="d-block f-14">Automation index (5–100)</label>
          <input aria-label="Automation index" type="number" min={0} max={100} className="form-control"
            value={v.automationIndex ?? 5} onChange={setNum("automationIndex")} />
        </div>
      </div>
    </div>
  );
}

function FavStar({ on, onClick }) {
  return (
    <span onClick={onClick} className="cursor-pointer" style={{ cursor: "pointer" }} title="Favourite">
      <svg width="18" height="18" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.5 1.5l2.35 4.76 5.25.76-3.8 3.7.9 5.23L9.5 13.7 4.8 16.65l.9-5.23-3.8-3.7 5.25-.76L9.5 1.5z"
          fill={on ? "#FFBF1B" : "#ddd"} stroke={on ? "#FFBF1B" : "#bbb"} strokeWidth="0.5" />
      </svg>
    </span>
  );
}

const CommunityPage = ({ view = "prospect", brandId: brandIdProp }) => {
  const usersPermissionRedux = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const brandsRedux = useSelector((s) => s.user.brands) || [];

  const FinalPermissions = computeFinalPermissions({
    userInfo: userInfoRedux,
    allPermission: allPermissionRedux,
    companyPermission: companyPermissionRedux,
    userPermission: usersPermissionRedux,
  });
  const canView = FinalPermissions?.includes(ALL_PERMISSIONS.COMMUNITY_VIEW);
  const canAdd = FinalPermissions?.includes(ALL_PERMISSIONS.COMMUNITY_ADD);
  const canEdit = FinalPermissions?.includes(ALL_PERMISSIONS.COMMUNITY_EDIT);
  const canDelete = FinalPermissions?.includes(ALL_PERMISSIONS.COMMUNITY_DELETE);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  const [discoverBrand, setDiscoverBrand] = useState(brandIdProp || "");
  const [credModal, setCredModal] = useState(null); // { loading, data }
  const [replyModal, setReplyModal] = useState(null); // { community, post, text, busy }
  const [catalog, setCatalog] = useState([]); // live-connector catalog (per channel fields)
  const [connectModal, setConnectModal] = useState(null); // { community, channel, config, secrets, missing, busy }
  const [chatModal, setChatModal] = useState(null); // { community, loading, messages, text, busy }
  const [addModal, setAddModal] = useState(null); // { value, busy } — manual add

  const MESSAGING_VIEWS = ["channels", "inbox", "contacts", "automation", "connectors", "analytics"];
  const isMessagingView = MESSAGING_VIEWS.includes(view);

  const load = useCallback(async () => {
    if (!canView || isMessagingView) return;
    setLoading(true);
    try {
      const res = await listCommunities({ status: view, brand_id: brandIdProp });
      setRows((res && res.data) || []);
    } catch (e) {
      addNotification(e?.error || "Failed to load communities", "danger");
    } finally {
      setLoading(false);
    }
  }, [canView, view, brandIdProp, isMessagingView]);

  useEffect(() => { load(); }, [load]);

  // The Active view offers live channel connectors — load the catalog once so
  // the Connect form knows what each channel needs.
  useEffect(() => {
    if (view !== "active" || !canView) return;
    let alive = true;
    getConnectorCatalog()
      .then((res) => { if (alive) setCatalog((res && res.data && res.data.channels) || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [view, canView]);

  if (!canView) {
    return (
      <div className="p-4">
        <h4 className="text-capitalize">Community</h4>
        <p>You don't have access to the Community feature. Request access from your administrator.</p>
      </div>
    );
  }

  // Messaging-hub tabs (Phases 0-4). All share the tab-bar + breadcrumb header.
  if (isMessagingView) {
    return (
      <div className="p-4">
        <CommunityTabs view={view} />
        {view === "channels" && <CommunityChannels brandId={brandIdProp} />}
        {view === "inbox" && <CommunityInbox brandId={brandIdProp} />}
        {view === "contacts" && <CommunityContacts brandId={brandIdProp} />}
        {view === "automation" && <CommunityAutomation brandId={brandIdProp} />}
        {view === "connectors" && <CommunityConnectors brandId={brandIdProp} />}
        {view === "analytics" && <CommunityAnalytics brandId={brandIdProp} />}
        {view === "jobs" && <JobPipeline brandId={brandIdProp} />}
      </div>
    );
  }

  const visible = favOnly ? rows.filter((r) => r.favourite) : rows;

  const onToggleFav = async (row) => {
    try {
      await toggleFavourite(row._id, !row.favourite);
      setRows((rs) => rs.map((r) => (r._id === row._id ? { ...r, favourite: !r.favourite } : r)));
    } catch (e) { addNotification(e?.error || "Failed to update favourite", "danger"); }
  };

  const onDelete = async (row) => {
    if (!canDelete) return;
    try {
      await deleteCommunity(row._id);
      setRows((rs) => rs.filter((r) => r._id !== row._id));
      addNotification("Community removed", "success");
    } catch (e) { addNotification(e?.error || "Failed to remove", "danger"); }
  };

  const onApprove = async (row) => {
    if (!canEdit) return;
    try {
      await approveCommunity(row._id);
      setRows((rs) => rs.filter((r) => r._id !== row._id));
      addNotification("Approved — moved to Active. Subscribing in the background.", "success");
    } catch (e) { addNotification(e?.error || "Failed to approve", "danger"); }
  };

  const onDiscover = async () => {
    if (!canEdit) return;
    const b = brandIdProp || discoverBrand;
    if (!b) { addNotification("Pick a brand to discover communities for", "danger"); return; }
    try {
      await discoverCommunities(b);
      addNotification("Discovery queued — prospects will appear shortly.", "success");
    } catch (e) { addNotification(e?.error || "Failed to start discovery", "danger"); }
  };

  const openAdd = () => setAddModal({ value: emptyCommunity(brandIdProp || discoverBrand), busy: false });

  const onAddField = (field, val) =>
    setAddModal((m) => (m ? { ...m, value: { ...m.value, [field]: val } } : m));

  const submitAdd = async () => {
    if (!addModal) return;
    const v = addModal.value || {};
    if (!v.name || !v.name.trim()) { addNotification("Community name is required", "danger"); return; }
    setAddModal((m) => ({ ...m, busy: true }));
    try {
      // Denormalise the brand name for the "which brand" column, mirroring the
      // crawler. Empty secrets are dropped so they don't overwrite anything.
      const brand = brandsRedux.find((b) => String(b._id) === String(v.brand_id));
      const payload = { ...v, brandName: brand ? brand.name : "" };
      if (!payload.password) delete payload.password;
      if (!payload.apiKey) delete payload.apiKey;
      await createCommunity(payload);
      addNotification(`"${v.name}" added.`, "success");
      setAddModal(null);
      // A manual add lands in Active; only reload when that's the current view.
      if (view === (v.status || "active")) load();
    } catch (e) {
      addNotification(e?.error || "Failed to add community", "danger");
      setAddModal((m) => ({ ...m, busy: false }));
    }
  };

  const openCreds = async (row) => {
    setCredModal({ loading: true, data: null, name: row.name });
    try {
      const res = await getCommunityCredentials(row._id);
      setCredModal({ loading: false, data: (res && res.data) || {}, name: row.name });
    } catch (e) {
      setCredModal(null);
      addNotification(e?.error || "Failed to load credentials", "danger");
    }
  };

  const openReply = (community, post) => setReplyModal({ community, post, text: "", busy: false });

  const submitReply = async () => {
    if (!replyModal) return;
    const { community, post, text } = replyModal;
    setReplyModal((m) => ({ ...m, busy: true }));
    try {
      // Draft via AI then send the (operator-confirmed) text. If the operator
      // typed their own text we send it directly.
      const r = await requestAiReply(community._id, { postUrl: post.url, instructions: text });
      if (text && r && r.data && r.data.replyId) {
        await sendReply(community._id, r.data.replyId, { replyText: text });
        addNotification("Reply sent.", "success");
      } else {
        addNotification("AI reply requested — review the draft in history before sending.", "success");
      }
      setReplyModal(null);
    } catch (e) {
      addNotification(e?.error || "Failed to reply", "danger");
      setReplyModal((m) => ({ ...m, busy: false }));
    }
  };

  const channelDef = (channel) => catalog.find((c) => c.channel === channel) || { fields: [], secrets: [] };

  const openConnect = (community) => {
    const channel = (community.connector && community.connector.channel) || LIVE_CHANNELS[0];
    setConnectModal({
      community, channel,
      config: { ...((community.connector && community.connector.config) || {}) },
      secrets: {}, missing: [], busy: false,
    });
  };

  const submitConnect = async () => {
    if (!connectModal) return;
    const { community, channel, config, secrets } = connectModal;
    setConnectModal((m) => ({ ...m, busy: true, missing: [] }));
    try {
      await connectChannel(community._id, { channel, config, secrets });
      addNotification("Connector saved — verifying the connection in the background.", "success");
      setConnectModal(null);
      load();
    } catch (e) {
      // The server returns `missing` (labels) when required config is absent —
      // surface exactly what the operator still needs to provide.
      const missing = (e && e.missing) || [];
      setConnectModal((m) => ({ ...m, busy: false, missing }));
      if (!missing.length) addNotification(e?.error || "Failed to connect", "danger");
    }
  };

  const onDisconnect = async (community) => {
    try {
      await disconnectChannel(community._id);
      addNotification("Disconnected.", "success");
      load();
    } catch (e) { addNotification(e?.error || "Failed to disconnect", "danger"); }
  };

  const openChat = async (community) => {
    setChatModal({ community, loading: true, messages: [], text: "", busy: false });
    try {
      const res = await getMessages(community._id);
      setChatModal({ community, loading: false, messages: (res && res.data) || [], text: "", busy: false });
    } catch (e) {
      setChatModal(null);
      addNotification(e?.error || "Failed to load messages", "danger");
    }
  };

  const submitChat = async () => {
    if (!chatModal || !chatModal.text.trim()) return;
    const { community, text } = chatModal;
    setChatModal((m) => ({ ...m, busy: true }));
    try {
      await sendMessage(community._id, text.trim());
      const res = await getMessages(community._id);
      setChatModal((m) => ({ ...m, messages: (res && res.data) || [], text: "", busy: false }));
    } catch (e) {
      addNotification(e?.error || "Failed to send", "danger");
      setChatModal((m) => ({ ...m, busy: false }));
    }
  };

  return (
    <div className="p-3">
      <CommunityTabs view={view} />
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="text-capitalize mb-0">
          {view === "active" ? "Active communities" : "Prospect communities"}
        </h4>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <label className="mb-0 mr-2">
            <input type="checkbox" checked={favOnly} onChange={(e) => setFavOnly(e.target.checked)} /> Favourites only
          </label>
          {!brandIdProp && (
            <select className="form-control form-control-sm" style={{ width: 180 }}
              value={discoverBrand} onChange={(e) => setDiscoverBrand(e.target.value)}>
              <option value="">Select brand…</option>
              {brandsRedux.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          )}
          {canAdd && (
            <button className="btn btn-sm btn-outline-primary" onClick={openAdd}>Add community</button>
          )}
          {canEdit && (
            <button className="btn btn-sm btn-primary" onClick={onDiscover}>Discover communities</button>
          )}
        </div>
      </div>

      {loading ? <p>Loading…</p> : visible.length === 0 ? (
        <p>No {view === "active" ? "active communities" : "prospects"} yet.{" "}
          {view === "prospect" && "Run Discover to crawl for a brand."}</p>
      ) : view === "prospect" ? (
        <ProspectsTable
          rows={visible} brandsRedux={brandsRedux}
          onToggleFav={onToggleFav} onApprove={onApprove} onDelete={onDelete}
          onCreds={openCreds} canEdit={canEdit} canDelete={canDelete} />
      ) : (
        <ActiveTable
          rows={visible} brandsRedux={brandsRedux}
          onToggleFav={onToggleFav} onDelete={onDelete} onReply={openReply}
          onCreds={openCreds} canEdit={canEdit} canDelete={canDelete}
          onConnect={openConnect} onDisconnect={onDisconnect} onChat={openChat} />
      )}

      <Modal title={`Credentials — ${credModal?.name || ""}`} open={!!credModal}
        onCancel={() => setCredModal(null)} footer={null} destroyOnClose>
        {credModal?.loading ? <p>Loading…</p> : credModal?.data && (
          <div>
            <p className="mb-1"><b>Login URL:</b> {credModal.data.loginUrl || "—"}</p>
            <p className="mb-1"><b>Username:</b> {credModal.data.username || "—"}</p>
            <p className="mb-1"><b>Password:</b> {credModal.data.password || "—"}</p>
            <p className="mb-1"><b>API connector:</b> {credModal.data.apiConnectorUrl || "—"}</p>
            <p className="mb-1"><b>API key:</b> {credModal.data.apiKey || "—"}</p>
            {credModal.data.loginNotes && <p className="text-muted mb-0">{credModal.data.loginNotes}</p>}
          </div>
        )}
      </Modal>

      <Modal title="Add community" open={!!addModal}
        onCancel={() => setAddModal(null)} okText="Add"
        confirmLoading={addModal?.busy} onOk={submitAdd} destroyOnClose width={560}>
        {addModal && (
          <CommunityEditForm value={addModal.value} onChange={onAddField} brands={brandsRedux} />
        )}
      </Modal>

      <Modal title="Reply to post" open={!!replyModal}
        onCancel={() => setReplyModal(null)} okText="Send / Draft"
        confirmLoading={replyModal?.busy} onOk={submitReply} destroyOnClose>
        {replyModal && (
          <div>
            <p className="mb-1"><b>Post:</b> <a href={replyModal.post.url} target="_blank" rel="noreferrer">{replyModal.post.title || replyModal.post.url}</a></p>
            <textarea className="form-control" rows={5}
              placeholder="Type your reply, or leave blank to request an AI draft for review."
              value={replyModal.text} onChange={(e) => { const _v = e.target.value; setReplyModal((m) => ({ ...m, text: _v })); }} />
          </div>
        )}
      </Modal>

      <Modal title="Connect live channel" open={!!connectModal}
        onCancel={() => setConnectModal(null)} okText="Connect"
        confirmLoading={connectModal?.busy} onOk={submitConnect} destroyOnClose>
        {connectModal && (
          <div>
            <label className="mb-1 d-block"><b>Channel</b></label>
            <select className="form-control mb-2" value={connectModal.channel}
              onChange={(e) => { const _v = e.target.value; setConnectModal((m) => ({ ...m, channel: _v, config: {}, secrets: {}, missing: [] })); }}>
              {LIVE_CHANNELS.map((ch) => <option key={ch} value={ch}>{PLATFORM_LABELS[ch]}</option>)}
            </select>
            {channelDef(connectModal.channel).connectVia && (
              <p className="text-muted small mb-2">Connects via {channelDef(connectModal.channel).connectVia}.</p>
            )}
            {(channelDef(connectModal.channel).fields || []).map((f) => (
              <div className="mb-2" key={f.key}>
                <label className="mb-1 d-block small">{f.label}{f.required ? " *" : ""}</label>
                <input className="form-control form-control-sm"
                  value={connectModal.config[f.key] || ""}
                  onChange={(e) => { const _v = e.target.value; setConnectModal((m) => ({ ...m, config: { ...m.config, [f.key]: _v } })); }} />
              </div>
            ))}
            {(channelDef(connectModal.channel).secrets || []).map((s) => (
              <div className="mb-2" key={s.key}>
                <label className="mb-1 d-block small">{s.label}{s.required ? " *" : ""}</label>
                <input type="password" className="form-control form-control-sm" autoComplete="new-password"
                  placeholder={connectModal.community.connector && connectModal.community.connector._hasSecret ? "•••••• (leave blank to keep)" : ""}
                  value={connectModal.secrets[s.key] || ""}
                  onChange={(e) => { const _v = e.target.value; setConnectModal((m) => ({ ...m, secrets: { ...m.secrets, [s.key]: _v } })); }} />
              </div>
            ))}
            {connectModal.missing && connectModal.missing.length > 0 && (
              <div className="alert alert-warning py-2 px-2 mb-0 small">
                Please provide: {connectModal.missing.join(", ")}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal title={`Live chat — ${chatModal?.community?.name || ""}`} open={!!chatModal}
        onCancel={() => setChatModal(null)} footer={null} destroyOnClose width={520}>
        {chatModal && (chatModal.loading ? <p>Loading…</p> : (
          <div>
            <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #eee", borderRadius: 4, padding: 8, marginBottom: 8 }}>
              {chatModal.messages.length === 0 ? <p className="text-muted mb-0">No messages yet.</p> : chatModal.messages.map((m) => (
                <div key={m._id} className={`mb-2 d-flex ${m.direction === "out" ? "justify-content-end" : "justify-content-start"}`}>
                  <span className={`px-2 py-1 rounded ${m.direction === "out" ? "bg-primary text-white" : "bg-light"}`} style={{ maxWidth: "80%" }}>
                    {m.text}
                    <span className="d-block small" style={{ opacity: 0.7 }}>{m.status}{m.error ? ` · ${m.error}` : ""}</span>
                  </span>
                </div>
              ))}
            </div>
            {canEdit && (
              <div className="d-flex" style={{ gap: 8 }}>
                <input className="form-control" placeholder="Type a message…" value={chatModal.text}
                  onChange={(e) => { const _v = e.target.value; setChatModal((m) => ({ ...m, text: _v })); }}
                  onKeyDown={(e) => { if (e.key === "Enter") submitChat(); }} />
                <button className="btn btn-primary" disabled={chatModal.busy || !chatModal.text.trim()} onClick={submitChat}>Send</button>
              </div>
            )}
          </div>
        ))}
      </Modal>
    </div>
  );
};

function brandNameFor(row, brandsRedux) {
  if (row.brandName) return row.brandName;
  const b = brandsRedux.find((x) => String(x._id) === String(row.brand_id));
  return b ? b.name : "—";
}

function ProspectsTable({ rows, brandsRedux, onToggleFav, onApprove, onDelete, onCreds, canEdit, canDelete }) {
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th></th><th>Name</th><th>Description</th><th>Brand</th><th>Platform / link</th>
            <th>Importance</th><th>Credentials</th><th>Automation</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._id}>
              <td><FavStar on={r.favourite} onClick={() => onToggleFav(r)} /></td>
              <td>{r.name}</td>
              <td style={{ maxWidth: 220 }}>{r.description}</td>
              <td>{brandNameFor(r, brandsRedux)}</td>
              <td>
                {PLATFORM_LABELS[r.platform] || r.platform}
                {r.apiConnectorUrl && <> · <a href={r.apiConnectorUrl} target="_blank" rel="noreferrer">{r.connectionType === "api" ? "API" : "sign up"}</a></>}
              </td>
              <td>{r.importance}</td>
              <td><button className="btn btn-link btn-sm p-0" onClick={() => onCreds(r)}>•••••• view</button></td>
              <td title="100 = fully automatable via API, 5 = manual headless login">{r.automationIndex}</td>
              <td className="text-nowrap">
                {canEdit && <button className="btn btn-sm btn-success mr-1" onClick={() => onApprove(r)}>Approve</button>}
                {canDelete && <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(r)}>✕</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CONNECTOR_BADGE = {
  connected: "badge-success", pending: "badge-warning",
  error: "badge-danger", disconnected: "badge-secondary",
};

function ConnectorCell({ r, onConnect, onDisconnect, onChat, canEdit }) {
  const c = r.connector;
  const live = c && c.channel;
  return (
    <td style={{ minWidth: 160 }}>
      {live ? (
        <>
          <span className="d-block">
            {PLATFORM_LABELS[c.channel] || c.channel}{" "}
            <span className={`badge ${CONNECTOR_BADGE[c.status] || "badge-secondary"}`}>{c.status}</span>
          </span>
          {c.status === "error" && c.lastError && <span className="d-block small text-danger">{c.lastError}</span>}
          {canEdit && (
            <span className="text-nowrap">
              <button className="btn btn-link btn-sm p-0 mr-2" onClick={() => onChat(r)}>chat</button>
              <button className="btn btn-link btn-sm p-0 mr-2" onClick={() => onConnect(r)}>edit</button>
              {c.status !== "disconnected" && <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => onDisconnect(r)}>disconnect</button>}
            </span>
          )}
        </>
      ) : (
        canEdit ? <button className="btn btn-sm btn-outline-primary" onClick={() => onConnect(r)}>Connect</button>
          : <span className="text-muted">—</span>
      )}
    </td>
  );
}

function ActiveTable({ rows, brandsRedux, onToggleFav, onDelete, onReply, onCreds, canEdit, canDelete, onConnect, onDisconnect, onChat }) {
  // Rank most-active first (by total replies across posts), then latest activity.
  const ranked = [...rows].sort((a, b) => activityScore(b) - activityScore(a));
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th></th><th>Name</th><th>Description</th><th>Brand</th>
            <th>Active posts / threads</th><th>Live channel</th><th>Latest activity</th><th>Automation</th><th></th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((r) => (
            <tr key={r._id}>
              <td><FavStar on={r.favourite} onClick={() => onToggleFav(r)} /></td>
              <td>{r.name}</td>
              <td style={{ maxWidth: 200 }}>{r.description}</td>
              <td>{brandNameFor(r, brandsRedux)}</td>
              <td style={{ maxWidth: 360 }}>
                {(r.posts || []).length === 0 ? <span className="text-muted">No active discussions yet</span> : (
                  <ul className="mb-0 pl-3">
                    {(r.posts || []).map((p) => (
                      <li key={p._id || p.url}>
                        <a href={p.url} target="_blank" rel="noreferrer">{p.title || p.url}</a>
                        {canEdit && <button className="btn btn-link btn-sm p-0 ml-2" onClick={() => onReply(r, p)}>reply</button>}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <ConnectorCell r={r} onConnect={onConnect} onDisconnect={onDisconnect} onChat={onChat} canEdit={canEdit} />
              <td>{latestActivity(r)}</td>
              <td>{r.automationIndex}</td>
              <td className="text-nowrap">
                <button className="btn btn-link btn-sm p-0 mr-2" onClick={() => onCreds(r)}>creds</button>
                {canDelete && <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(r)}>✕</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function activityScore(r) {
  return (r.posts || []).reduce((s, p) => s + (p.replyCount || 0) + (p.relevance || 0), 0);
}
function latestActivity(r) {
  const dates = (r.posts || []).map((p) => p.lastActivityAt).filter(Boolean).map((d) => new Date(d).getTime());
  if (!dates.length) return "—";
  return new Date(Math.max(...dates)).toLocaleString();
}

export default CommunityPage;
